import * as THREE from 'three';
import { frontendLogger } from '../../shared/logger';

export interface BotConfig {
  id: string;
  name: string;
  difficulty: 'easy' | 'medium' | 'hard';
  position: THREE.Vector3;
  health: number;
  team?: string;
}

export interface BotBehavior {
  accuracy: number; // 0-1
  reactionTime: number; // ms
  movementSpeed: number; // multiplier
  aggressiveness: number; // 0-1
  aimShakeAmount: number; // random aim offset
  decisionInterval: number; // ms between AI decisions
}

const BOT_BEHAVIORS: Record<string, BotBehavior> = {
  easy: {
    accuracy: 0.3,
    reactionTime: 1000,
    movementSpeed: 0.7,
    aggressiveness: 0.3,
    aimShakeAmount: 0.3,
    decisionInterval: 2000
  },
  medium: {
    accuracy: 0.6,
    reactionTime: 500,
    movementSpeed: 0.9,
    aggressiveness: 0.6,
    aimShakeAmount: 0.15,
    decisionInterval: 1000
  },
  hard: {
    accuracy: 0.85,
    reactionTime: 200,
    movementSpeed: 1.0,
    aggressiveness: 0.85,
    aimShakeAmount: 0.05,
    decisionInterval: 500
  }
};

// Bot name pool for variety
const BOT_NAMES = [
  'AlphaBot', 'BetaKiller', 'GammaRay', 'DeltaForce', 'EpsilonOne',
  'ZetaHunter', 'ThetaSniper', 'OmegaDoom', 'SigmaElite', 'LambdaGhost',
  'NoobMaster', 'ProGamer', 'AimBot9000', 'FragMachine', 'HeadshotKing',
  'CampMaster', 'RunNGun', 'SneakyPete', 'RocketMan', 'BulletStorm'
];

export class Bot {
  public id: string;
  public name: string;
  public position: THREE.Vector3;
  public rotation: THREE.Euler;
  public velocity: THREE.Vector3;
  public health: number;
  public maxHealth: number;
  public team?: string;
  
  private behavior: BotBehavior;
  private target: string | null = null;
  private lastDecisionTime: number = 0;
  private lastShotTime: number = 0;
  // private moveDirection: THREE.Vector3; // For future use
  private isMoving: boolean = false;
  private isShooting: boolean = false;
  private patrolTarget: THREE.Vector3 | null = null;
  
  // State machine
  private state: 'idle' | 'patrol' | 'chase' | 'attack' | 'retreat' = 'idle';
  
  constructor(config: BotConfig) {
    this.id = config.id;
    this.name = config.name;
    this.position = config.position.clone();
    this.rotation = new THREE.Euler(0, Math.random() * Math.PI * 2, 0);
    this.velocity = new THREE.Vector3();
    this.health = config.health;
    this.maxHealth = config.health;
    this.team = config.team;
    this.behavior = BOT_BEHAVIORS[config.difficulty];
    // this.moveDirection = new THREE.Vector3(); // For future use
  }
  
  update(deltaTime: number, players: Map<string, any>, _obstacles: Map<string, any>) {
    const now = Date.now();
    
    // Make decisions at intervals based on difficulty
    if (now - this.lastDecisionTime > this.behavior.decisionInterval) {
      this.makeDecision(players, _obstacles);
      this.lastDecisionTime = now;
    }
    
    // Execute current state
    switch (this.state) {
      case 'idle':
        this.executeIdle();
        break;
      case 'patrol':
        this.executePatrol(deltaTime);
        break;
      case 'chase':
        this.executeChase(deltaTime, players);
        break;
      case 'attack':
        this.executeAttack(deltaTime, players);
        break;
      case 'retreat':
        this.executeRetreat(deltaTime);
        break;
    }
    
    // Update position based on velocity
    this.position.add(this.velocity.clone().multiplyScalar(deltaTime));
    
    // Apply friction
    this.velocity.multiplyScalar(0.9);
    
    // Clamp to world bounds
    this.position.x = Math.max(0, Math.min(64, this.position.x));
    this.position.z = Math.max(0, Math.min(64, this.position.z));
  }
  
  private makeDecision(players: Map<string, any>, _obstacles: Map<string, any>) {
    // Find nearest enemy player
    let nearestEnemy: any = null;
    let nearestDistance = Infinity;
    
    players.forEach((player, playerId) => {
      if (playerId === this.id) return; // Skip self
      if (this.team && player.team === this.team) return; // Skip teammates
      
      const distance = this.position.distanceTo(player.position);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestEnemy = player;
      }
    });
    
    // State transitions based on situation
    if (nearestEnemy && nearestDistance < 30) {
      // Enemy nearby
      if (this.health < this.maxHealth * 0.3) {
        // Low health - retreat
        this.state = 'retreat';
        this.target = null;
      } else if (nearestDistance < 15) {
        // Close range - attack
        this.state = 'attack';
        this.target = nearestEnemy.id;
      } else {
        // Mid range - chase
        this.state = 'chase';
        this.target = nearestEnemy.id;
      }
    } else {
      // No enemies nearby - patrol
      this.state = 'patrol';
      this.target = null;
    }
  }
  
  private executeIdle() {
    // Stand still, look around
    this.rotation.y += (Math.random() - 0.5) * 0.1;
    this.isMoving = false;
    this.isShooting = false;
  }
  
  private executePatrol(deltaTime: number) {
    // Random patrol movement
    if (!this.patrolTarget || this.position.distanceTo(this.patrolTarget) < 2) {
      // Pick new patrol target
      this.patrolTarget = new THREE.Vector3(
        Math.random() * 60 + 2,
        this.position.y,
        Math.random() * 60 + 2
      );
    }
    
    // Move towards patrol target
    const direction = this.patrolTarget.clone().sub(this.position).normalize();
    this.velocity.add(direction.multiplyScalar(this.behavior.movementSpeed * 5 * deltaTime));
    
    // Face movement direction
    this.rotation.y = Math.atan2(direction.x, direction.z);
    this.isMoving = true;
    this.isShooting = false;
  }
  
  private executeChase(deltaTime: number, players: Map<string, any>) {
    if (!this.target) {
      this.state = 'patrol';
      return;
    }
    
    const targetPlayer = players.get(this.target);
    if (!targetPlayer) {
      this.target = null;
      this.state = 'patrol';
      return;
    }
    
    // Move towards target
    const direction = targetPlayer.position.clone().sub(this.position).normalize();
    this.velocity.add(direction.multiplyScalar(this.behavior.movementSpeed * 8 * deltaTime));
    
    // Face target
    this.rotation.y = Math.atan2(direction.x, direction.z);
    this.isMoving = true;
    
    // Shoot if in range
    const distance = this.position.distanceTo(targetPlayer.position);
    if (distance < 20) {
      this.attemptShoot();
    }
  }
  
  private executeAttack(deltaTime: number, players: Map<string, any>) {
    if (!this.target) {
      this.state = 'patrol';
      return;
    }
    
    const targetPlayer = players.get(this.target);
    if (!targetPlayer) {
      this.target = null;
      this.state = 'patrol';
      return;
    }
    
    // Strafe around target
    const toTarget = targetPlayer.position.clone().sub(this.position);
    const distance = toTarget.length();
    toTarget.normalize();
    
    // Strafe perpendicular to target
    const strafe = new THREE.Vector3(-toTarget.z, 0, toTarget.x);
    strafe.multiplyScalar((Math.random() - 0.5) * 2);
    
    // Move to maintain optimal distance (10-15 units)
    if (distance > 15) {
      this.velocity.add(toTarget.multiplyScalar(this.behavior.movementSpeed * 5 * deltaTime));
    } else if (distance < 10) {
      this.velocity.sub(toTarget.multiplyScalar(this.behavior.movementSpeed * 3 * deltaTime));
    }
    
    // Add strafe movement
    this.velocity.add(strafe.multiplyScalar(this.behavior.movementSpeed * 4 * deltaTime));
    
    // Aim at target with some inaccuracy
    const aimOffset = new THREE.Vector3(
      (Math.random() - 0.5) * this.behavior.aimShakeAmount,
      (Math.random() - 0.5) * this.behavior.aimShakeAmount * 0.5,
      (Math.random() - 0.5) * this.behavior.aimShakeAmount
    );
    
    const aimDirection = toTarget.add(aimOffset);
    this.rotation.y = Math.atan2(aimDirection.x, aimDirection.z);
    
    this.isMoving = true;
    this.attemptShoot();
  }
  
  private executeRetreat(deltaTime: number) {
    // Find cover or run away from danger
    if (!this.patrolTarget) {
      // Pick a retreat position (corners are good)
      const corners = [
        new THREE.Vector3(5, this.position.y, 5),
        new THREE.Vector3(59, this.position.y, 5),
        new THREE.Vector3(5, this.position.y, 59),
        new THREE.Vector3(59, this.position.y, 59)
      ];
      
      // Find nearest corner
      let nearestCorner = corners[0];
      let minDist = Infinity;
      corners.forEach(corner => {
        const dist = this.position.distanceTo(corner);
        if (dist < minDist) {
          minDist = dist;
          nearestCorner = corner;
        }
      });
      
      this.patrolTarget = nearestCorner;
    }
    
    // Sprint to retreat position
    const direction = this.patrolTarget.clone().sub(this.position).normalize();
    this.velocity.add(direction.multiplyScalar(this.behavior.movementSpeed * 10 * deltaTime));
    
    // Face movement direction
    this.rotation.y = Math.atan2(direction.x, direction.z);
    this.isMoving = true;
    this.isShooting = false;
    
    // Stop retreating when health recovers or reached safe spot
    if (this.health > this.maxHealth * 0.5 || this.position.distanceTo(this.patrolTarget) < 3) {
      this.state = 'patrol';
      this.patrolTarget = null;
    }
  }
  
  private attemptShoot() {
    const now = Date.now();
    const fireRate = 200; // ms between shots
    
    if (now - this.lastShotTime > fireRate) {
      // Check accuracy roll
      if (Math.random() < this.behavior.accuracy) {
        this.isShooting = true;
        this.lastShotTime = now;
      }
    }
  }
  
  takeDamage(amount: number) {
    this.health -= amount;
    
    // React to being shot
    if (this.state === 'patrol' || this.state === 'idle') {
      // Alert! Switch to combat mode
      this.state = 'chase';
    }
    
    if (this.health <= 0) {
      this.onDeath();
    }
  }
  
  onDeath() {
    frontendLogger.info(`Bot ${this.name} died`);
    // Will be handled by game mode manager
  }
  
  respawn(position: THREE.Vector3) {
    this.position.copy(position);
    this.health = this.maxHealth;
    this.state = 'idle';
    this.target = null;
    this.velocity.set(0, 0, 0);
    frontendLogger.info(`Bot ${this.name} respawned`);
  }
  
  getMovementInput() {
    if (!this.isMoving) return { forward: 0, right: 0, jump: false };
    
    // Convert movement direction to input
    const forward = Math.cos(this.rotation.y);
    const right = Math.sin(this.rotation.y);
    
    return {
      forward: forward * this.velocity.length() * 0.1,
      right: right * this.velocity.length() * 0.1,
      jump: Math.random() < 0.05 // Random jumping
    };
  }
  
  getAimDirection() {
    return new THREE.Vector3(
      -Math.sin(this.rotation.y),
      0,
      -Math.cos(this.rotation.y)
    );
  }
  
  shouldShoot() {
    const shoot = this.isShooting;
    this.isShooting = false; // Reset after checking
    return shoot;
  }
}

export class BotManager {
  private bots: Map<string, Bot> = new Map();
  private botNames: Set<string> = new Set();
  
  constructor() {
    frontendLogger.info('BotManager initialized');
  }
  
  createBot(difficulty: 'easy' | 'medium' | 'hard', position?: THREE.Vector3): Bot {
    const id = `bot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const name = this.generateUniqueName();
    
    const bot = new Bot({
      id,
      name,
      difficulty,
      position: position || new THREE.Vector3(
        Math.random() * 60 + 2,
        10,
        Math.random() * 60 + 2
      ),
      health: 100
    });
    
    this.bots.set(id, bot);
    return bot;
  }
  
  private generateUniqueName(): string {
    let name: string;
    do {
      name = BOT_NAMES[Math.floor(Math.random() * BOT_NAMES.length)];
    } while (this.botNames.has(name));
    
    this.botNames.add(name);
    return name;
  }
  
  removeBot(id: string) {
    const bot = this.bots.get(id);
    if (bot) {
      this.botNames.delete(bot.name);
      this.bots.delete(id);
    }
  }
  
  updateBots(deltaTime: number, players: Map<string, any>, obstacles: Map<string, any>) {
    this.bots.forEach(bot => {
      bot.update(deltaTime, players, obstacles);
    });
  }
  
  getBot(id: string): Bot | undefined {
    return this.bots.get(id);
  }
  
  getAllBots(): Bot[] {
    return Array.from(this.bots.values());
  }
  
  clear() {
    this.bots.clear();
    this.botNames.clear();
  }
}
